<script lang="ts">
	import type { SelectCourse, SelectCourseCategory, SelectFile } from '$db/tables';
	import { Separator } from '$lib/components/ui/separator';
	import { PUBLIC_BASE_URL } from '$env/static/public';
	import { Badge } from '$lib/components/ui/badge';
	import { Image } from '@unpic/svelte';
	import { parseDate, today } from '@internationalized/date';
	import Star from '@lucide/svelte/icons/star';

	interface Props {
		data: {
			image: SelectFile;
			course: SelectCourse;
			category: SelectCourseCategory;
			averageRating: number;
		};
		sportspass: boolean | undefined;
	}

	let { data, sportspass = false }: Props = $props();
</script>

<a
	href={!sportspass
		? `/ausbildungen/${data.category.slug}/${data.course.slug}`
		: `/partner/sportspass/${data.course.slug}`}
	class="group w-full overflow-clip rounded-md bg-secondary transition-all ease-in-out hover:scale-[1.02]"
>
	<div class="grid h-full w-full md:grid-cols-[2fr_5fr]">
		<div class="relative w-full overflow-x-clip bg-asge-800">
			<div
				class="pointer-events-none absolute right-0 z-10 h-full w-full translate-y-px scale-x-105 bg-gradient-to-b from-transparent to-asge-50 md:translate-x-px md:translate-y-0 md:scale-x-100 md:scale-y-125 md:bg-gradient-to-r"
			></div>
			<Image
				class="h-full w-full object-cover object-center opacity-85"
				cdn="directus"
				src="{PUBLIC_BASE_URL}/api/img?objectKey={data.image.objectKey}"
				layout="constrained"
				width={800}
				height={400}
				alt={data.course.name}
			/>
		</div>
		<div class="h-full w-full p-4 pl-6">
			<div class="flex h-full flex-col gap-2">
				<div class="flex flex-col items-start justify-between gap-3 md:flex-row md:gap-8">
					<div>
						<div class="hyphens-auto text-pretty text-lg font-semibold">
							{data.course.name}
						</div>
						<div class="line-clamp-1 text-sm">
							{data.category.name}
						</div>
					</div>
					<div
						class="flex items-center justify-center rounded-md bg-white px-2 py-1 font-semibold text-asge-600"
					>
						{new Intl.NumberFormat('de-DE', {
							style: 'currency',
							currency: 'EUR'
						}).format(
							!sportspass
								? data.course.priceInCents / 100
								: data.course.sportspassPriceInCents! / 100
						)}
					</div>
				</div>
				<Separator class="my-2 bg-asge-100 md:my-0" />
				<div class="flex items-center justify-between gap-4">
					<div class="flex items-center gap-2">
						<div class="whitespace-nowrap text-sm">
							{data.course.defaultDuration}
							{data.course.defaultDuration > 1 ? 'Tage' : 'Tag'}
						</div>
						<Separator orientation="vertical" class="mx-2 h-4 bg-asge-100" />
						{#if data.averageRating}
							<div class="flex items-center gap-1 text-sm">
								<Star class="size-4" fill="#FFD932" color="#FFD932" />
								{data.averageRating}
							</div>
						{/if}
					</div>
					<div class="flex flex-wrap items-center justify-end gap-x-2 gap-y-1">
						{#if data.course.stateRegistrationId}
							<div class="flex items-center">
								<Badge class="whitespace-nowrap bg-asge-200 hover:bg-asge-200" variant="secondary"
									>Staatlich zugelassen</Badge
								>
							</div>
						{/if}
						{#if parseDate(data.course.createdAt.split(' ')[0]) > today('Europe/Berlin').subtract( { weeks: 2 } )}
							<div class="flex items-center">
								<Badge class="whitespace-nowrap bg-orange-200" variant="secondary">Neu</Badge>
							</div>
						{/if}
					</div>
				</div>
			</div>
		</div>
	</div>
</a>
